// ATTICUS-ANALYTICS-COMMAND-CENTER-1 — executive analytics command center.
//
// A read-only, interactive dashboard surface with six role lenses plus the
// personal WIP view. Lenses are capability-gated (ordinary adjusters only ever
// see "My Numbers"). All firm-wide data is TPA-walled + cap-gated server-side;
// the client gates are defense-in-depth + UX. No metric is faked — anything not
// computable from reliable native data renders an explicit unavailable card.
import React, { useEffect, useMemo, useState } from "react";
import { can, apiFetch } from "../api";
import MyNumbers from "./MyNumbers";
import { AnalyticsFilters, DrilldownDrawer, useDrilldown, presetFrom, todayISO } from "../analytics/AnalyticsKit";
import { ANALYTICS_CSS } from "../analytics/analyticsStyles";
import ExecutiveLens from "../analytics/lenses/ExecutiveLens";
import FinancialLens from "../analytics/lenses/FinancialLens";
import OperationsLens from "../analytics/lenses/OperationsLens";
import ComplianceLens from "../analytics/lenses/ComplianceLens";
import AccountingLens from "../analytics/lenses/AccountingLens";
import CommercialLens from "../analytics/lenses/CommercialLens";

// Executive lenses are restricted to senior roles (VIEW_ANALYTICS = ownership /
// executive / system_admin). Every other employee sees only "My Numbers".
const LENSES = [
  { id: "executive", label: "Executive", icon: "◆", gate: () => can("view_analytics") },
  { id: "financial", label: "Financial", icon: "$", gate: () => can("view_analytics") },
  { id: "operations", label: "Operations", icon: "⚙", gate: () => can("view_analytics") },
  { id: "compliance", label: "Compliance", icon: "⚖", gate: () => can("view_analytics") },
  { id: "accounting", label: "Accounting", icon: "▤", gate: () => can("view_analytics") },
  { id: "commercial", label: "Commercial", icon: "◈", gate: () => can("view_analytics") },
  { id: "personal", label: "My Numbers", icon: "★", gate: () => true },
];

export default function Analytics({ user, onNavigateToClaim }) {
  const lenses = useMemo(() => LENSES.filter((l) => l.gate()), []);
  const [active, setActive] = useState(lenses[0]?.id || "personal");
  const [filters, setFilters] = useState({
    preset: "90d", date_from: presetFrom("90d"), date_to: todayISO(),
    business_line: null, claim_type: null, status: null, matter_type: null, adjuster_id: null,
  });
  const [adjusters, setAdjusters] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const { drill, openDrill, closeDrill } = useDrilldown(filters);

  // Adjuster facet for the (team-gated) filter — harmless 403 → empty list.
  useEffect(() => {
    if (!can("view_team_reports")) return;
    apiFetch("/claims/lead-adjusters").then((d) => {
      setAdjusters(Array.isArray(d) ? d : (d.adjusters || d.rows || []));
    }).catch(() => {});
  }, []);

  const firmwide = active !== "personal";
  const lensProps = { filters, openDrill, onOpenClaim: onNavigateToClaim, reloadKey, user };

  return (
    <div className="an-page">
      <style>{ANALYTICS_CSS}</style>

      <div className="an-header">
        <div>
          <div className="an-h-title">Analytics</div>
          <div className="an-h-sub">Executive command center — claims, financials & operations</div>
        </div>
        <div className="an-lenstabs">
          {lenses.map((l) => (
            <button key={l.id} className={`an-lenstab${active === l.id ? " active" : ""}`}
                    onClick={() => setActive(l.id)}>
              <span className="an-lenstab-ic">{l.icon}</span>{l.label}
            </button>
          ))}
        </div>
      </div>

      {firmwide && (
        <AnalyticsFilters value={filters} onChange={setFilters} adjusters={adjusters}
                          refreshedAt={null} onRefresh={() => setReloadKey((k) => k + 1)} />
      )}

      <div className="an-lens">
        {active === "executive" && <ExecutiveLens {...lensProps} />}
        {active === "financial" && <FinancialLens {...lensProps} />}
        {active === "operations" && <OperationsLens {...lensProps} />}
        {active === "compliance" && <ComplianceLens {...lensProps} />}
        {active === "accounting" && <AccountingLens {...lensProps} />}
        {active === "commercial" && <CommercialLens {...lensProps} />}
        {active === "personal" && <MyNumbers user={user} />}
      </div>

      <DrilldownDrawer drill={drill} onClose={closeDrill} onOpenClaim={onNavigateToClaim} />
    </div>
  );
}
